import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BOARD } from "../../data/board";
import { MAP, clamp } from "../../utils/mapCamera";
import { useCamera } from "./CameraProvider";

const EMPTY = { sections: {}, anchors: {} };
const TOLERANCE = 2.5;

const BoardGeometryContext = createContext(null);

/**
 * Reads where sections and pinned cards actually landed, in board units. Rects are compared
 * against the board's own rect, so the result is the same at any zoom level and in either
 * zoom mode (CSS zoom or transform scale).
 */
const readGeometry = (board) => {
  const frame = board.getBoundingClientRect();
  const unit = frame.width / BOARD.width;
  if (!unit) return EMPTY;

  const toBoard = (node) => {
    const rect = node.getBoundingClientRect();
    return {
      x: (rect.left - frame.left) / unit,
      y: (rect.top - frame.top) / unit,
      w: rect.width / unit,
      h: rect.height / unit,
    };
  };

  const sections = {};
  board.querySelectorAll(".board-section[id]").forEach((node) => {
    sections[node.id] = toBoard(node);
  });

  // A card hangs from its pin, and so does every string tied to it. Pins are never pushed in
  // dead centre, so read the pin itself; cards without one are tied at their top centre.
  const anchors = {};
  board.querySelectorAll("[data-node]").forEach((node) => {
    const pin = node.querySelector(".evidence-pin");
    const rect = toBoard(pin ?? node);
    anchors[node.dataset.node] = pin
      ? { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 }
      : { x: rect.x + rect.w / 2, y: rect.y + 2 };
  });

  return { sections, anchors };
};

const closeEnough = (a, b) => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => {
    const other = b[key];
    if (!other) return false;
    return Object.keys(a[key]).every((field) => Math.abs(a[key][field] - other[field]) <= TOLERANCE);
  });
};

export const BoardGeometryProvider = ({ children }) => {
  const [board, setBoard] = useState(null);
  const [geometry, setGeometry] = useState(EMPTY);

  useEffect(() => {
    if (!board) return undefined;
    let raf = 0;
    let live = true;

    const measure = () => {
      raf = 0;
      if (!live) return;
      const next = readGeometry(board);
      setGeometry((prev) =>
        closeEnough(prev.sections, next.sections) && closeEnough(prev.anchors, next.anchors) ? prev : next
      );
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    measure();

    // Late fonts and logos reflow cards after first paint; re-read whenever a section changes size.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    board.querySelectorAll(".board-section").forEach((node) => observer?.observe(node));
    board.addEventListener("load", schedule, true);
    document.fonts?.ready?.then(schedule).catch(() => {});

    return () => {
      live = false;
      if (raf) cancelAnimationFrame(raf);
      observer?.disconnect();
      board.removeEventListener("load", schedule, true);
    };
  }, [board]);

  const value = useMemo(() => ({ ...geometry, board, registerBoard: setBoard }), [board, geometry]);

  return <BoardGeometryContext.Provider value={value}>{children}</BoardGeometryContext.Provider>;
};

export const useBoardGeometry = () => {
  const ctx = useContext(BoardGeometryContext);
  if (!ctx) {
    throw new Error("useBoardGeometry must be used inside BoardGeometryProvider");
  }
  return ctx;
};

/** Flies the camera to a section, zoomed so the section (or as much as is readable) fits. */
export const useFlyToSection = () => {
  const { sections } = useBoardGeometry();
  const { flyToBoard, vw, vh } = useCamera();

  return useCallback(
    (id) => {
      const rect = sections[id];
      if (!rect) return;
      const viewW = vw.get();
      const viewH = vh.get();
      const fit = Math.min((viewW * 0.92) / rect.w, (viewH * 0.8) / rect.h);
      const scale = clamp(fit, Math.max(MAP.minScale, 0.75), 1);
      // Tall sections open at their top rather than their middle.
      const visibleH = Math.min(rect.h, (viewH * 0.8) / scale);
      flyToBoard(rect.x + rect.w / 2, rect.y + visibleH / 2, scale, true);
    },
    [flyToBoard, sections, vh, vw]
  );
};
